/**
 * Admin V3 — Incidents — top-level page
 *
 * Mounted at: /admin-v3/incidents (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Operational incident log (read-only).
 * Mirror of legacy IncidentManagementPage (AdminControlTowerV2.js:1262-1535).
 *
 * SCOPE FOR PHASE 2.9:
 *   - GET /admin/control-tower/incidents (paginated, server-side status + type filter)
 *   - 4-card stats summary (total / open / in_progress / resolved)
 *   - Client-side search by title + description over loaded page
 *   - Per-row expansion with description, root_event, event_chain, context,
 *     resolution_notes, resolved_by, resolved_at, intervention_id
 *   - Row click → /admin-v3/incidents/:id (placeholder until future phase
 *     replaces the DetailPlaceholder route)
 *   - Entity column: linked to /admin/bookings/:id when entity_type === "booking"
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No acknowledge / resolve / escalate / update buttons (Phase 3+).
 *   - No POST endpoints called.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, AlertCircle as AlertCircleIcon, CheckCircle2, Activity } from 'lucide-react';
import { SectionCard, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import IncidentsListSearchBar from './IncidentsListSearchBar';
import IncidentsListTable from './IncidentsListTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 50;  // backend default; max 200

const StatBlock = ({ icon: Icon, label, value, alertTone = false, testId }) => (
  <div
    className={cn(
      'rounded-2xl border bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.04)]',
      alertTone ? 'border-rose-200' : 'border-slate-200'
    )}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <div
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-lg',
          alertTone ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500'
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
    </div>
    <div className={cn('text-2xl font-bold', alertTone ? 'text-rose-700' : 'text-slate-900')}>{value}</div>
  </div>
);

const IncidentsListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [status, setStatus] = useState('');
  const [incidentType, setIncidentType] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = { limit: DEFAULT_LIMIT };
  if (status)       queryParams.status         = status;
  if (incidentType) queryParams.incident_type  = incidentType;

  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/control-tower/incidents',
    queryParams
  );

  const allIncidents = Array.isArray(data?.incidents) ? data.incidents : [];
  const stats = data?.stats || { total: 0, open: 0, in_progress: 0, resolved: 0 };

  // Client-side search by title/description over loaded page
  const incidents = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allIncidents;
    return allIncidents.filter((i) =>
      (i.title || '').toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q)
    );
  }, [allIncidents, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(status) || Boolean(incidentType);
  const isAtBackendCap = allIncidents.length === DEFAULT_LIMIT;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('incidents.title', locale)}
      icon={AlertTriangle}
      iconClass="bg-amber-100 text-amber-600"
    >
      <div className="space-y-4">
        <IncidentsListSearchBar
          status={status}
          onStatusChange={setStatus}
          incidentType={incidentType}
          onIncidentTypeChange={setIncidentType}
          search={search}
          onSearchChange={setSearch}
          resultsCount={incidents.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        {/* Stats grid */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adminv3-incidents-stats">
            <StatBlock
              icon={Activity}
              label={t('incidents.stats.total', locale)}
              value={stats.total ?? 0}
              testId="adminv3-incidents-stat-total"
            />
            <StatBlock
              icon={AlertCircleIcon}
              label={t('incidents.stats.open', locale)}
              value={stats.open ?? 0}
              alertTone={(stats.open ?? 0) > 0}
              testId="adminv3-incidents-stat-open"
            />
            <StatBlock
              icon={Activity}
              label={t('incidents.stats.inProgress', locale)}
              value={stats.in_progress ?? 0}
              testId="adminv3-incidents-stat-in-progress"
            />
            <StatBlock
              icon={CheckCircle2}
              label={t('incidents.stats.resolved', locale)}
              value={stats.resolved ?? 0}
              testId="adminv3-incidents-stat-resolved"
            />
          </div>
        )}

        <IncidentsListTable
          incidents={incidents}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-incidents-cap-hint"
          >
            {t('incidents.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default IncidentsListPage;
